/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { InfoBannerUtils } from '../../../views/VideoPlayerUI/FullInfoBanner/EventDescription/InfoBannerUtils';
import { DeviceModel } from '../../device';
import { BrandingUiInfoKeys } from '../../entities/BrandingUiInfoKeys';
import { AppError } from '../../error/AppError';
import { ServicesClientImpl } from '../../impls/ServicesClientImpl';
import { Locale } from '../../locale/Locale';
import { LocaleKeys } from '../../locale/LocaleKeys';
import { Log } from '../../logging';
import { SocuDataModelImpl } from '../../socu/impls';
import { VisualDisplayTypes } from '../enums';
import { ILiveTvErrorBase } from '../interfaces';
import { VideoPlayerUtils } from '../VideoPlayerUtils';

export class LiveTvErrorBase implements ILiveTvErrorBase {
    TAG: string = 'LiveTvErrorBase';

    protected priority: number = 0;

    protected title: string = '';

    protected description: string = '';

    protected uiErrorCode: string = '';

    protected uiVisualType: VisualDisplayTypes = 0;

    private service = new ServicesClientImpl();

    constructor(error: AppError) {
        this.processErrorByCode(error);
    }

    public formatDescription() {
        Log.i(this.TAG, 'formatDescription, desription:', this.description);
        if (
            this.description.includes('URL_ERROR') ||
            this.description.includes('EXISTING_IP') ||
            this.description.includes('DEVICE_NAME') ||
            this.description.includes('CUSTOMER_SUPPORT_NAME') ||
            this.description.includes('DISTRIBUTOR_NAME') ||
            this.description.includes('PHONE_CUSTOMER_SUPPORT_NUMBER') ||
            this.description.includes('URL_CUSTOMER_SUPPORT') ||
            this.description.includes('NAME_OF_SHOW') ||
            this.description.includes('PLAYBACK_TYPE')
        ) {
            // TODO: Utils should be avoided
            const airingOffers = VideoPlayerUtils.getInstance().getAiringData();
            const offerPreview = VideoPlayerUtils.getInstance().getPreviewData();

            let formattedDescription = this.description;
            const dataFormatter = new InfoBannerUtils(airingOffers, offerPreview);
            formattedDescription = formattedDescription.replace(
                'URL_ERROR',
                this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_ERROR),
            );
            // TODO: Replace with WAN IP [KST-2852]
            formattedDescription = formattedDescription.replace(
                'EXISTING_IP',
                DeviceModel.getTVProperties().getIpAddress(),
            );
            formattedDescription = formattedDescription.replace(
                'DEVICE_NAME_SHORT',
                this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.DEVICE_NAME_SHORT),
            );
            formattedDescription = formattedDescription.replace(
                'DEVICE_NAME',
                this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.DEVICE_NAME),
            );
            formattedDescription = formattedDescription.replace(
                'DISTRIBUTOR_NAME',
                this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.DISTRIBUTOR_NAME),
            );
            formattedDescription = formattedDescription.replace(
                'PHONE_CUSTOMER_SUPPORT_NUMBER',
                this.service.getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
                ),
            );
            formattedDescription = formattedDescription.replace(
                'CUSTOMER_SUPPORT_NAME',
                this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME),
            );
            formattedDescription = formattedDescription.replace(
                'URL_CUSTOMER_SUPPORT',
                this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_CUSTOMER_SUPPORT),
            );
            formattedDescription = formattedDescription.replace(
                'NAME_OF_SHOW',
                dataFormatter.getChannelTitleText(),
            );
            let replaceValue = '';
            if (SocuDataModelImpl.getInstance().isStartOver()) {
                replaceValue = this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER);
            } else if (SocuDataModelImpl.getInstance().isCatchUp()) {
                replaceValue = this.service.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP);
            } else {
                replaceValue = Locale.getString(
                    LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_LIVE_TV_STRING,
                );
            }
            formattedDescription = formattedDescription.replace('PLAYBACK_TYPE', replaceValue);
            this.description = formattedDescription;
            Diagnostics.info(
                this.TAG,
                'formatDescription, formatted description:',
                formattedDescription,
            );
            Log.i(this.TAG, 'formatDescription, formatted description:', formattedDescription);
        }
    }

    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    public processErrorByCode(error: any) {
        // Diagnostics.info(this.TAG, 'derived class must override processErrorByCode', error);
        // Log.i(this.TAG, 'derived class must override processErrorByCode', error);
        throw new Error('Method not impemented.');
    }

    // eslint-disable-next-line class-methods-use-this
    public showVisualDisplay(): void {
        // PlaybackErrorDisplay.getInstance().showVisualDisplay(this);
        throw new Error('Method not implemented.');
    }

    public getDescription(): string {
        this.formatDescription();
        return this.description;
    }

    public getPriority(): number {
        return this.priority;
    }

    public getTitle(): string {
        return this.title;
    }

    public getVisualDisplayType(): VisualDisplayTypes {
        return this.uiVisualType;
    }

    public getUiErrorCode(): string {
        return this.uiErrorCode;
    }

    public toJson() {
        return {
            priority: this.priority,
            title: this.title,
            description: this.description,
            uiErrorCode: this.uiErrorCode,
            uiVisualType: this.uiVisualType,
        };
    }
}
